import * as React from "react";

/**
 * Button variant styles.
 */
const buttonVariants = {
  variant: {
    default:
      "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-lg",
    destructive:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline:
      "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:shadow-md",
    brandOutline:
      "border-2 border-primary bg-background text-primary shadow-sm hover:bg-primary hover:text-primary-foreground",
    secondary:
      "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
    brand: "bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:shadow-lg",
    brandSecondary: "bg-secondary text-secondary-foreground shadow hover:brightness-90 transition-all duration-200",
  },
  size: {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  },
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual variant of the button.
   * @default "default"
   */
  variant?: keyof typeof buttonVariants.variant;

  /**
   * Size variant of the button.
   * @default "default"
   */
  size?: keyof typeof buttonVariants.size;

  /**
   * Whether the button is in a loading state.
   */
  isLoading?: boolean;
}

/**
 * Button component with multiple variants and sizes.
 *
 * Based on shadcn/ui Button pattern, adapted for this project.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      variant = "default",
      size = "default",
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer hover:scale-[1.02] active:scale-[0.98]";

    return (
      <button
        className={`${baseStyles} ${buttonVariants.variant[variant]} ${buttonVariants.size[size]} ${className}`}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
